import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from '../../Actions/axios.config';
import {
  HiOutlineHeart, HiHeart, HiOutlineBookmark, HiBookmark,
  HiOutlineChatBubbleOvalLeft, HiOutlineArrowLeft,
  HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineShare
} from "react-icons/hi2";
import UserAvatar from "../UserAvatar/UserAvatar";
import CommentCard from '../CommentCard/CommentCard';
import { useSelector } from 'react-redux';
import LoadingSpinner from '../Loading/LoadingSpinner';
import { Footer, ServiceNotice } from '..';
import { logPostView } from '../../Actions/analytics';

function PostView() {
  const navigate = useNavigate();
  const { postId } = useParams();

  const [isOwner, setIsOwner] = useState(false);
  const [post, setPost] = useState({});
  const [loading, setLoading] = useState(true);

  const [like, setLike] = useState(false);
  const [bookmark, setBookmark] = useState(false);

  const [count, setCount] = useState(0);
  const [likers, setLikers] = useState([]);
  const [showLikers, setShowLikers] = useState(false); 

  const [showComments, setShowComments] = useState(false); 
  const [comments, setComments] = useState([]);

  const [comment, setComment] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const [copySuccess, setCopySuccess] = useState(false);
  
  // Extract isAuthenticated along with user
  const { user, isAuthenticated } = useSelector((state) => state.user);
  const hasLoggedView = useRef(false);

  const fetchComments = async () => {
    try {
      const res = await axios.get(`/posts/getComment/${postId}`);
      setComments(res.data.comments.slice().reverse());
    } catch (error) {
      console.error("Failed to fetch comments", error);
    }
  };

  useEffect(() => {
    const storedAccessToken = localStorage.getItem('access_token');
    if (storedAccessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedAccessToken}`;
    }

    axios.get(`/posts/getPosts/${postId}`)
      .then((response) => {
        setPost(response.data.post);
        setBookmark(response.data.isBookmarked);
        setIsOwner(response.data.isOwner);
        setLoading(false);

        if (response.data.post && response.data.post.company && !hasLoggedView.current) {
          logPostView(response.data.post.company);
          hasLoggedView.current = true;
        }
      })
      .catch(() => {
        setLoading(false);
        navigate('/posts');
      });

    axios.get(`/posts/getLikers/${postId}`).then((res) => {
      setLike(res.data.currLike);
      setLikers(res.data.likers);
      setCount(res.data.likers.length);
    }).catch(() => {});

    fetchComments();
  }, [postId, navigate]);


  useEffect(() => {
    const hash = window.location.hash;
    if (!loading && hash === '#discussion') {
      requestAnimationFrame(() => {
        const el = document.getElementById('discussion');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [loading]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const onLikeHit = async () => {
    if (!isAuthenticated) return;
    setLike(!like);
    setCount(prev => like ? prev - 1 : prev + 1);
    await axios.get(`/posts/likePost/${postId}`);
  };

  const onBookmarkHit = async () => {
    if (!isAuthenticated) return;
    setBookmark(!bookmark);
    await axios.get(`/posts/bookmarkPost/${postId}`);
  };


  const handleAddComment = async (e) => {
    e.preventDefault();

    const text = comment.trim();
    if (!text) return;

    try {
      setIsSubmittingComment(true);

      await axios.post('/posts/addComment', {
        text,
        post: postId,
        createdAt: new Date().toISOString(),
      });

      setComment('');
      await fetchComments();

      requestAnimationFrame(() => {
        const el = document.getElementById('discussion');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to add comment');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen font-sans selection:bg-blue-100 relative">
      {/* 1. Header Navigation */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/posts')}
            className="flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold transition-colors"
          >
            <HiOutlineArrowLeft size={20} />
            <span className="hidden sm:inline">Back</span>
          </button>

          <div className="flex items-center gap-2">
            {isOwner && isAuthenticated && (
              <>
                <Link
                  to={`/create/${post._id}`}
                  className="text-sm font-bold text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-xl transition-all"
                >
                  Edit
                </Link>
                <button
                  type="button"
                  className="text-sm font-bold text-red-500 hover:bg-red-50 px-4 py-2 rounded-xl transition-all"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-12 lg:py-20 relative">
        {/* 2. Headline & Metadata */}
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-md">
              {post.company}
            </span>
            <span className="bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-md">
              {post.type}
            </span>
            <div
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-black text-[10px] uppercase tracking-widest ${
                post.status === "Selected" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
            >
              {post.status === "Selected" ? <HiOutlineCheckCircle size={14} /> : <HiOutlineXCircle size={14} />}
              {post.status}
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl font-black text-slate-900 leading-[1.1] mb-8 tracking-tight">
            {post.caption}
          </h1>

          <div className="flex items-center gap-4 py-6 border-y border-slate-100">
            <Link to={`/user/${post.owner?._id}`}>
              <UserAvatar src={post.owner?.profileImage} name={post.owner?.name} size={44} className="ring-2 ring-slate-100" />
            </Link>
            <div className="flex-grow">
              <Link
                to={`/user/${post.owner?._id}`}
                className="block font-black text-slate-900 text-lg hover:text-blue-600 transition-colors leading-none mb-1"
              >
                {post.owner?.name}
              </Link>
              <p className="text-sm text-slate-500 font-medium italic">
                {post.owner?.branch} • {post.owner?.graduationYear}
              </p>
            </div>
          </div>
        </header>

        {/* 3. Content Body */}
        <article
          className={`prose prose-slate prose-lg max-w-none 
          prose-headings:text-slate-900 prose-headings:font-black 
          prose-p:text-slate-700 prose-p:leading-[1.8]
          prose-strong:text-slate-900 prose-strong:font-bold
          prose-blockquote:border-l-4 prose-blockquote:border-blue-500 prose-blockquote:bg-blue-50/40 prose-blockquote:rounded-r-2xl prose-blockquote:not-italic
          ${isAuthenticated ? 'mb-20' : 'mb-0 relative'}`}
        >
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        </article>

        {/* Medium-style Login Banner overlay for Unauthenticated Users */}
        {!isAuthenticated && (
          <div className="relative -mt-12 pt-12 pb-16">
            <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-transparent to-white pointer-events-none z-10"></div>
            
            <div className="relative z-20 bg-slate-50 border border-slate-200 shadow-sm rounded-[2.5rem] p-8 md:p-12 text-center max-w-2xl mx-auto mt-4">
              <h3 className="text-3xl font-black text-slate-900 mb-4 tracking-tight">Read the full story</h3>
              <p className="text-slate-600 font-medium mb-8 text-lg leading-relaxed">
                Log in to read the complete interview experience, unlock exclusive campus-specific insights, and engage with the community.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/register" className="w-full sm:w-auto px-8 py-3.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 active:scale-95">
                  Create an account
                </Link>
                <Link to="/login" className="w-full sm:w-auto px-8 py-3.5 bg-white text-slate-700 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition-all active:scale-95">
                  Log in
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* 4. Action Bar */}
        <div className="flex items-center justify-between py-6 border-t border-slate-100 mb-10">
          <div className="flex items-center gap-4 sm:gap-8">
            <button 
              type="button" 
              onClick={isAuthenticated ? onLikeHit : undefined} 
              className={`flex items-center gap-2 group ${!isAuthenticated ? 'cursor-default opacity-70' : ''}`}
              title={!isAuthenticated ? 'Log in to like this post' : ''}
            >
              {like ? (
                <HiHeart size={28} className={`text-red-500 ${isAuthenticated ? 'animate-bounce-short' : ''}`} />
              ) : (
                <HiOutlineHeart size={28} className={`text-slate-400 ${isAuthenticated ? 'group-hover:text-red-400 transition-colors' : ''}`} />
              )}
              <span className={`font-black text-lg ${like ? 'text-red-500' : 'text-slate-400'}`}>{count}</span>
            </button>

            {isAuthenticated ? (
              <a href="#discussion" className="flex items-center gap-2 group">
                <HiOutlineChatBubbleOvalLeft size={28} className="text-slate-400 group-hover:text-blue-500 transition-colors" />
                <span className="font-black text-lg text-slate-400">{comments.length}</span>
              </a>
            ) : (
              <div className="flex items-center gap-2 opacity-70 cursor-default" title="Log in to view comments">
                <HiOutlineChatBubbleOvalLeft size={28} className="text-slate-400" />
                <span className="font-black text-lg text-slate-400">{comments.length}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={handleShare}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${
                copySuccess ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <HiOutlineShare size={18} />
              {copySuccess ? 'Link Copied!' : 'Share'}
            </button>

            <button
              type="button"
              onClick={isAuthenticated ? onBookmarkHit : undefined}
              className={`p-2 rounded-xl transition-all ${!isAuthenticated ? 'cursor-default opacity-70 text-slate-400' : bookmark ? 'text-blue-600' : 'text-slate-400 hover:text-blue-500'}`}
              title={!isAuthenticated ? 'Log in to bookmark this post' : ''}
            >
              {bookmark ? <HiBookmark size={28} /> : <HiOutlineBookmark size={28} />}
            </button>
          </div>
        </div>

        {/* 5. Comments Section - Visible only if Authenticated */}
        {isAuthenticated && (
          <section id="discussion" className="space-y-8 animate-in fade-in duration-500">
            <h3 className="text-2xl font-black text-slate-900 tracking-tight">Discussion</h3>

            <form onSubmit={handleAddComment} className="bg-slate-50 border border-slate-100 rounded-2xl p-4">
              <div className="flex gap-3 items-start">
                <UserAvatar src={user?.profileImage} name={user?.name} size={36} className="mt-1" />
                <div className="flex-1">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    placeholder="Write a comment..."
                    disabled={isSubmittingComment}
                    className="w-full resize-none bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-200 transition-shadow"
                  />

                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-slate-400 font-medium">
                      {comment.trim().length}/500
                    </p>

                    <button
                      type="submit"
                      disabled={isSubmittingComment || !comment.trim()}
                      className="px-5 py-2 rounded-xl bg-blue-600 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors active:scale-95"
                    >
                      {isSubmittingComment ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <div className="space-y-6">
              {comments.map((c) => <CommentCard key={c._id} comment={c} />)}
              {comments.length === 0 && <p className="text-slate-400 font-medium italic">No comments yet. Start the conversation!</p>}
            </div>
          </section>
        )}
      </main>

      <ServiceNotice />
      <Footer />
    </div>
  );
}

export default PostView;